// this is a demo feature not intended to go to PROD
import * as cols from '@/modules/common/components/ag-table/columns/common';
import { ColDef } from 'ag-grid-enterprise';
import { alignLeft, alignRight, component } from './utils';
import { rateAdapter } from '@/modules/common/components/ag-table/columns/common';
import TruncateText from '@/modules/common/components/TruncateText.vue';
import { h } from 'vue';
import { Venue } from '@/utils/api/finra-transactions';
import { VBtn, VIcon } from 'vuetify/lib';
import { FinraTransaction } from '@/modules/common/models';
export {
  checkbox,
  ticker,
  cusip,
  rateAdapter,
} from '@/modules/common/components/ag-table/columns/common';

const truncateTextAdapter = component<{ value: string; maxWidth?: string }>(
  (props) => () => h(TruncateText, { props })
);

export const transactAt = (): ColDef<FinraTransaction> =>
  cols.timestamp({ field: 'transactAt', headerName: 'Transact Time' });

export const reportedAt = (): ColDef<FinraTransaction> =>
  cols.timestamp({ field: 'reportedAt', headerName: 'Report Time' });

export const reportType = (): ColDef<FinraTransaction> => ({
  field: 'reportType',
  colId: 'reportType',
  headerName: 'Report Type',
  ...alignLeft(),
});

export const displayId = (): ColDef<FinraTransaction> => ({
  field: 'displayId',
  colId: 'displayId',
  headerName: 'Reporting ID',
  cellRendererSelector: (params) =>
    params.node?.rowPinned
      ? undefined
      : truncateTextAdapter({
          value: params.value,
          maxWidth: '8rem',
        }),
  ...alignLeft(),
});

export const issuer = (): ColDef<FinraTransaction> => ({
  field: 'issuer',
  colId: 'issuer',
  headerName: 'Issuer',
  cellRendererSelector: (params) =>
    params.node?.rowPinned
      ? undefined
      : truncateTextAdapter({
          value: params.value,
          maxWidth: '10.25rem',
        }),
  ...alignLeft(),
});

export const lei = (): ColDef<FinraTransaction> => ({
  field: 'lei',
  colId: 'lei',
  headerName: 'Lei',
  cellRendererSelector: (params) =>
    params.node?.rowPinned
      ? undefined
      : truncateTextAdapter({
          value: params.value,
          maxWidth: '8.5rem',
        }),
  ...alignLeft(),
});

export const quantity = (): ColDef<FinraTransaction> => ({
  ...cols.quantity({
    field: 'quantity',
    headerName: 'Quantity',
  }),
});

export function rate(): ColDef<FinraTransaction> {
  return {
    ...cols.rate(),
    headerName: 'Rate / Fee',
    cellRendererSelector: (params) =>
      params.node?.rowPinned || !params.data
        ? undefined
        : rateAdapter({
            rate: params.data.rate,
            rateModifier: params.data.rateModifier,
            precision: undefined,
          }),
  };
}

export const marketValue = (): ColDef<FinraTransaction> => ({
  ...cols.price({
    field: 'marketValue',
    headerName: 'Market Value',
  }),
});

export const collateralType = (): ColDef<FinraTransaction> => ({
  field: 'collateralType',
  colId: 'collateralType',
  headerName: 'Collateral Type',
  cellRendererSelector: (params) =>
    params.node?.rowPinned
      ? undefined
      : truncateTextAdapter({
          value: params.value,
          maxWidth: '11.50rem',
        }),
  ...alignLeft(),
});

export const collateralValue = (): ColDef<FinraTransaction> => ({
  ...cols.price({
    field: 'collateralValue',
    headerName: 'Collateral Value',
  }),
});

export function collateralPct(): ColDef<FinraTransaction> {
  return {
    ...cols.rate(),
    field: 'collateralPct',
    colId: 'collateralPct',
    headerName: '% Collateralized',
    cellRendererSelector: (params) =>
      params.node?.rowPinned
        ? undefined
        : rateAdapter({
            rate: params.value,
            rateModifier: undefined,
            precision: undefined,
          }),
  };
}

export function venue(): ColDef<FinraTransaction> {
  return {
    field: 'venue',
    colId: 'venue',
    headerName: 'Venue',
    ...alignLeft(),
  };
}

export const dueDate = (): ColDef<FinraTransaction> =>
  cols.date({ field: 'endsAt', headerName: 'Due Date' });

export function counterparty(): ColDef<FinraTransaction> {
  return {
    field: 'counterpartyName',
    colId: 'counterpartyName',
    headerName: 'Counterparty',
    ...alignLeft(),
  };
}

export function counterpartyType(): ColDef<FinraTransaction> {
  return {
    field: 'counterpartyType',
    colId: 'counterpartyType',
    headerName: 'Counterparty Type',
    ...alignLeft(),
  };
}

export function ftd(): ColDef<FinraTransaction> {
  return {
    field: 'ftd',
    colId: 'ftd',
    headerName: 'FTD',
    ...alignLeft(),
  };
}

// just the icons, no click events, demo purposes only
const actionsAdapter = component<{
  item: FinraTransaction;
}>(
  (props) => () =>
    h('div', { class: 'actions d-flex justify-end', style: 'gap: 0.5rem' }, [
      props.item.venue === Venue.FinraVenueProvable &&
        h(
          VBtn,
          {
            class: 'icon-action',
            attrs: {
              'data-test': 'view-loan',
              icon: true,
              'x-small': true,
            },
          },
          [h(VIcon, { attrs: { size: '24' } }, 'mdi-eye')]
        ),
      h(
        VBtn,
        {
          class: 'icon-action',
          attrs: {
            'data-test': 'edit-transaction',
            icon: true,
            'x-small': true,
          },
        },
        [h(VIcon, { attrs: { size: '24' } }, 'mdi-pencil')]
      ),
    ])
);

export function actions(): ColDef<FinraTransaction> {
  return {
    colId: 'actions',
    headerName: 'Actions',
    cellRendererSelector: (params) =>
      params.node?.rowPinned
        ? undefined
        : actionsAdapter({
            item: params.data!,
          }),
    pinned: 'right',
    lockVisible: true,
    suppressColumnsToolPanel: true,
    ...alignRight(),
  };
}
